/**
 * Payment provider abstraction.
 *
 * UI/checkout code MUST go through the registry — never instantiate
 * providers directly. This keeps swapping AZUL/Stripe/Paddle later a
 * one-line change.
 */

export type ProviderId = "azul" | "manual_transfer" | "cod";

export type PaymentStatus =
  | "pending"
  | "authorized"
  | "captured"
  | "failed"
  | "refunded"
  | "cancelled";

export interface CreatePaymentInput {
  orderId: string;
  userId: string;
  amount: number;
  currency?: "DOP";
  idempotencyKey: string;
  metadata?: Record<string, unknown>;
}

export interface CreatePaymentResult {
  transactionId: string;
  providerRef: string | null;
  status: PaymentStatus;
  /** If the provider needs a hosted page (AZUL Página de Pago, Stripe Checkout). */
  redirectUrl?: string;
  /** Human-readable next-step instructions for manual flows. */
  instructions?: {
    title: string;
    steps: string[];
    bankAccounts?: BankAccount[];
    reference: string;
  };
}

export interface BankAccount {
  bank: string;
  account_type: string;
  account_number: string;
  holder: string;
  rnc?: string;
}

export interface PaymentProvider {
  id: ProviderId;
  label: { es: string; en: string };
  icon: string;
  isAvailable: (opts?: { city?: string; settings?: PaymentSettings }) => boolean;
  createPayment: (input: CreatePaymentInput) => Promise<CreatePaymentResult>;
}

export interface PaymentSettings {
  bank_accounts: BankAccount[];
  cod_enabled: boolean;
  cod_surcharge: number;
  cod_allowed_cities: string[];
  azul_enabled: boolean;
  manual_transfer_enabled: boolean;
  /** Minimum cart subtotal (USD) for free in-area Express shipping. */
  express_free_threshold_usd: number;
  /** Explanatory note shown under the shipping selector (Spanish). */
  express_free_note_es: string | null;
  /** Explanatory note shown under the shipping selector (English). */
  express_free_note_en: string | null;
}

export const DEFAULT_PAYMENT_SETTINGS: PaymentSettings = {
  bank_accounts: [],
  cod_enabled: true,
  cod_surcharge: 100,
  cod_allowed_cities: [
    "Santo Domingo",
    "Distrito Nacional",
    "Santo Domingo Este",
    "Santo Domingo Norte",
    "Santo Domingo Oeste",
  ],
  azul_enabled: false,
  manual_transfer_enabled: true,
  express_free_threshold_usd: 50,
  express_free_note_es:
    "El envío Express en área es gratis cuando tu subtotal alcanza el monto mínimo configurado por la tienda.",
  express_free_note_en:
    "In-area express shipping is free when your subtotal reaches the store-defined minimum.",
};